import { StrictMode, type ComponentType } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter, createRootRoute, createRoute } from '@tanstack/react-router'
import { HelmetProvider } from 'react-helmet-async'
import './index.css'
import { RootLayout } from './routes/__root'
import { HomePage } from './routes/index'
import { LoginPage } from './routes/login'
import { RegisterPage } from './routes/register'
import { ForgotPasswordPage } from './routes/forgot-password'
import { ResetPasswordPage } from './routes/reset-password'
import { AcceptInvitePage } from './routes/accept-invite'
import { CompleteRegistrationPage } from './routes/complete-registration'
import { DashboardPage } from './routes/dashboard'
import { RoomsPage } from './routes/rooms'
import { RoomCreatePage } from './routes/room-create'
import { EditRoomPage } from './routes/room-form'
import { BuildingsPage } from './routes/buildings'
import { BuildingFormPage } from './routes/building-form'
import { SettingsPage } from './routes/settings'
import { SettingsTermsPage } from './routes/settings-terms'
import { PliCertificatesPage } from './routes/pli-certificates'
import { BookingsPage } from './routes/bookings'
import { MyBookingsPage } from './routes/my-bookings'
import { PeoplePage } from './routes/people'
import { AdminPage } from './routes/admin'
import { AdminStripePage } from './routes/admin-stripe'
import { MarketplacePage } from './routes/marketplace'
import { MarketplaceOrgPage } from './routes/marketplace-org'
import { MarketplaceRoomPage } from './routes/marketplace-room'
import { MarketplaceCityPage } from './routes/marketplace-city'
import { RequestsPage } from './routes/requests'
import { AuditPage } from './routes/audit'
import { InvoicesPage } from './routes/invoices'
import { useAuthStore } from './lib/auth'

// Remount a manage page whenever the managed organisation changes so all of its
// data loaders re-run against the newly selected org - no full page reload needed.
function withOrgKey<P extends object>(Component: ComponentType<P>) {
  return function OrgKeyedPage(props: P) {
    const orgId = useAuthStore((s) => s.currentOrg?.orgId)
    return <Component key={orgId || 'no-org'} {...props} />
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

// Define routes manually instead of using generated file
const rootRoute = createRootRoute({
  component: RootLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomePage,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>) => ({
    returnTo: typeof search.returnTo === 'string' ? search.returnTo : undefined,
  }),
})

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
})

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
})

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/accept-invite',
  component: AcceptInvitePage,
})

const completeRegistrationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/complete-registration',
  component: CompleteRegistrationPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
})

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: withOrgKey(DashboardPage),
})

const roomsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/rooms',
  component: withOrgKey(RoomsPage),
})

const buildingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/buildings',
  component: withOrgKey(BuildingsPage),
})

const newBuildingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/buildings/new',
  component: withOrgKey(BuildingFormPage),
})

const editBuildingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/buildings/$buildingId/edit',
  component: withOrgKey(BuildingFormPage),
})

const newRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/rooms/new',
  component: withOrgKey(RoomCreatePage),
})

const EditRoomPageKeyed = withOrgKey(EditRoomPage)

const editRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/rooms/$roomId/edit',
  component: function EditRoomWrapper() {
    const { roomId } = editRoomRoute.useParams()
    return <EditRoomPageKeyed roomId={roomId} />
  },
})

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/settings',
  component: withOrgKey(SettingsPage),
  validateSearch: (search: Record<string, unknown>) => ({
    stripe: typeof search.stripe === 'string' ? search.stripe : undefined,
  }),
})

const settingsTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/settings/terms',
  component: withOrgKey(SettingsTermsPage),
})

const pliCertificatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/pli-certificates',
  component: PliCertificatesPage,
})

// Redirect old route
const pliCertificatesRedirectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/insurance',
  component: function InsuranceRedirect() {
    window.location.replace('/dashboard/pli-certificates')
    return null
  },
})

const bookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/bookings',
  component: withOrgKey(BookingsPage),
})

// Personal cross-org bookings page - the only bookings page guests ever see
const myBookingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-bookings',
  component: MyBookingsPage,
})

const requestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/requests',
  component: withOrgKey(RequestsPage),
})

const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/people',
  component: withOrgKey(PeoplePage),
})

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/audit',
  component: withOrgKey(AuditPage),
})

const invoicesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard/invoices',
  component: withOrgKey(InvoicesPage),
})

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminPage,
})

const adminStripeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/stripe',
  component: AdminStripePage,
})

const marketplaceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/marketplace',
  component: MarketplacePage,
})

const marketplaceCityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/marketplace/city/$citySlug',
  component: MarketplaceCityPage,
})

const marketplaceOrgRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/marketplace/$orgSlug',
  component: MarketplaceOrgPage,
})

const marketplaceRoomRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/marketplace/$orgSlug/$roomSlug',
  component: MarketplaceRoomPage,
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInviteRoute,
  completeRegistrationRoute,
  dashboardRoute,
  roomsRoute,
  buildingsRoute,
  newBuildingRoute,
  editBuildingRoute,
  newRoomRoute,
  editRoomRoute,
  settingsRoute,
  settingsTermsRoute,
  pliCertificatesRoute,
  pliCertificatesRedirectRoute,
  bookingsRoute,
  myBookingsRoute,
  requestsRoute,
  peopleRoute,
  auditRoute,
  invoicesRoute,
  adminRoute,
  adminStripeRoute,
  marketplaceRoute,
  marketplaceCityRoute,
  marketplaceOrgRoute,
  marketplaceRoomRoute,
])

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  },
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')!

createRoot(rootElement).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>
)
